import * as React from 'react';
import { ReactNode, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';

export interface InteractiveTab {
    id: string;
    label: string;
    color: string;
    icon: ReactNode;
    component: ReactNode;
}

type Props = {
    tabs: InteractiveTab[]
};
const InteractiveTabsComponent = ({ tabs }: Props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isOpen, setIsOpen] = React.useState(false);

    const [activeTab, setActiveTab] = useState(tabs[0].id);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
        setProgress((currentIndex / (tabs.length - 1)) * 100);
    }, [activeTab]);

    const handleTabClick = (tabId: string) => {
        setActiveTab(tabId);
        searchParams.set('mode', tabId);
        setSearchParams(searchParams);
        setIsOpen(false);
    };

    React.useEffect(() => {
        if (searchParams.get('mode')) {
            setActiveTab(searchParams.get('mode') || tabs[0].id);
        }
    }, [searchParams]);

    const getTabContent = () => {
        return tabs.find(tab => tab.id === activeTab)?.component || null;
    };

    const activeTabData = tabs.find(tab => tab.id === activeTab);


    return (
        <div className="">
            <div className=" relative border-b">
                <div className="relative md:hidden">
                    <motion.button
                        className="w-full p-2 text-left text-gray-800 bg-white rounded-lg flex items-center justify-between relative z-10"
                        onClick={() => setIsOpen(!isOpen)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <div className="flex items-center space-x-2">

                            <div
                                className={`w-8 h-8 rounded-full text-white font-semibold text-sm focus:outline-none transition-all duration-300 ease-in-out transform hover:scale-110 flex items-center justify-center ${
                                    activeTab === activeTabData?.id ? 'ring-4 ring-offset-4 ring-opacity-50' : ''
                                }`}
                                style={{
                                    backgroundColor: activeTabData?.color,
                                    boxShadow: `0 0 0 2px white, 0 0 0 4px ${activeTabData?.color}`,
                                }}
                                aria-label={`${activeTabData?.label} tab`}
                            >
                                {activeTabData?.icon}

                            </div>

                            <span className="font-semibold">{activeTabData?.label}</span>
                        </div>
                        <motion.div
                            animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ChevronUp className="w-5 h-5" />
                        </motion.div>
                    </motion.button>
                    <AnimatePresence>
                        {isOpen && (
                            <motion.div
                                className="absolute w-full mt-2 bg-white rounded-lg shadow-lg overflow-hidden z-20"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                            >
                                {tabs.map((item, index) => (

                                    <motion.div
                                        key={index}
                                        className={`w-full p-3 text-left flex items-center border-b space-x-2 hover:bg-gray-100`}
                                        onClick={() => handleTabClick(item.id)}
                                        // disabled={item.disabled}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <button
                                            className={`w-8 h-8 rounded-full text-white font-semibold text-sm focus:outline-none transition-all duration-300 ease-in-out transform hover:scale-110 flex items-center justify-center ${
                                                activeTab === item.id ? 'ring-4 ring-offset-4 ring-opacity-50' : ''
                                            }`}
                                            style={{
                                                backgroundColor: item.color,
                                                boxShadow: `0 0 0 2px white, 0 0 0 4px ${item.color}`,
                                            }}
                                            aria-label={`${item.label} tab`}
                                        >
                                            {item.icon}

                                        </button>
                                        <span className=" font-medium">{item.label}</span>
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <div
                    className="hidden md:flex justify-between items-center relative z-10 p-8 ">
                    {tabs.map((tab) => {

                        return (
                            <div key={tab.id} className="flex flex-col items-center">
                                <button
                                    onClick={() => handleTabClick(tab.id)}
                                    className={`w-14 h-14 rounded-full text-white font-semibold text-sm focus:outline-none transition-all duration-300 ease-in-out transform hover:scale-110 flex items-center justify-center ${
                                        activeTab === tab.id ? 'ring-4 ring-offset-4 ring-opacity-50' : ''
                                    }`}
                                    style={{
                                        backgroundColor: tab.color,
                                        boxShadow: `0 0 0 2px white, 0 0 0 4px ${tab.color}`,
                                    }}
                                    aria-label={`${tab.label} tab`}
                                >
                                    {tab.icon}
                                </button>
                                <span className="mt-2 text-xs font-medium">{tab.label}</span>
                            </div>
                        );
                    })}
                </div>
                <div
                    className="hidden md:flex h-1 bg-gray-200 absolute top-14 left-10 right-10 z-0 rounded-full overflow-hidden">
                    <motion.div
                        className="h-full bg-gradient-to-r from-[#E11D48] to-[#00B4D8]"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5, ease: 'easeInOut' }}
                    />
                </div>
            </div>
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="my-3   min-h-[200px]"
                >
                    {getTabContent()}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default InteractiveTabsComponent;